import React, { useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "../firebase";
import { styled, keyframes } from "@mui/material/styles";
import {
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Fade,
} from "@mui/material";

import {
  Google,
  ArrowBack,
} from "@mui/icons-material";
import { withErrorBoundary } from "react-error-boundary";
import GeneralErrorPage from "./GeneralErrorPage";
import logo from "../assets/brand-logo.svg";

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: "38px",
  marginBottom: "8px",
  background: "linear-gradient(90deg, #2E7D32 0%, #1B5E20 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "left",
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: "#666",
  fontSize: "16px",
  lineHeight: 1.6,
  marginBottom: "40px",
  textAlign: "left",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5, 3),
  height: "56px",
  width: "100%",
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "15px",
  fontWeight: 500,
  boxShadow: "none",
  border: "1px solid #eee",
  backgroundColor: "#fff",
  color: "#1a1a1a",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#f8f9fa",
    borderColor: "#66BB6A",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  },
}));

const slideAnimation = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-50%, 0, 0);
  }
`;

function SignInScreen() {
  const [isLoading, setIsLoading] = useState(true);
  const [loader, setLoader] = useState({
    googleLoading: false,
  });
  const [error, setError] = useState({
    message: "",
    open: false,
  });

  const logos = Array(5).fill({ src: logo });
  const duplicatedLogos = [
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
  ];

  useEffect(() => {
    setIsLoading(false);
  }, []);


  const handleGoogleLogin = () => {
    setLoader({ googleLoading: true });
    const auth = firebase.auth();
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    auth
      .signInWithPopup(googleProvider)
      .catch((error) => {
        const fallbackCodes = [
          "auth/popup-closed-by-user",
          "auth/popup-blocked",
          "auth/cancelled-popup-request",
        ];
        if (fallbackCodes.includes(error.code)) {
          // Fallback to redirect flow for browsers that block/close popups
          setLoader({ googleLoading: true });
          return auth.signInWithRedirect(googleProvider);
        }
        setLoader({ googleLoading: false });
        setError({ message: error.message, open: true });
        setIsLoading(false);
      });
  };



  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#fff",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      <Grid container>
        {/* Left side - Dark Background with Content */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            background: "#2E7D32",
            color: "#fff",
            p: 8,
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 30% 30%, rgba(102, 187, 106, 0.3) 0%, rgba(102, 187, 106, 0.1) 45%, rgba(46, 125, 50, 0.2) 100%)",
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2 }}>
            <img
              src={logo}
              alt="logo"
              style={{
                height: "32px",
                filter: "brightness(0) invert(1)",
                marginBottom: "80px",
              }}
            />
            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontWeight: 800,
                fontSize: { md: "48px", lg: "56px" },
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
            >
              Track Your Life
            </Typography>
            <Typography
              sx={{
                mb: 3,
                color: "rgba(255,255,255,0.9)",
                fontSize: "20px",
                lineHeight: 1.6,
                maxWidth: "480px",
              }}
            >
              Beautiful counters to monitor your daily progress, track streaks, record metrics, and journal your journey.
            </Typography>
          </Box>

          {/* Logo Carousel */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              height: "80px",
              mt: "auto",
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "14px",
                mb: 2,
                display: "none",
              }}
            >
              Trusted by companies worldwide
            </Typography>
            <Box
              sx={{
                display: "flex",
                position: "absolute",
                animation: `${slideAnimation} 30s linear infinite`,
                alignItems: "center",
                gap: 6,
              }}
            >
              {duplicatedLogos.map((logo, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "160px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={logo.src}
                    alt="company logo"
                    style={{
                      height: "32px",
                      filter: "brightness(0) invert(1)",
                      opacity: 0.6,
                      transition: "opacity 0.2s ease-in-out",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Right side - Login Form */}
        <Fade in={true} timeout={1000}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, sm: 3, md: 8 },
              maxWidth: { xs: "100%", md: "760px" },
              margin: "0 auto",
              position: "relative",
              height: { xs: "100vh", md: "auto" },
              overflowY: { xs: "auto", md: "visible" },
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <IconButton
              onClick={() => { /* no full reloads */ }}
              sx={{
                alignSelf: "flex-start",
                color: "#2E7D32",
                mb: 6,
                "&:hover": {
                  backgroundColor: "rgba(102, 187, 106, 0.08)",
                },
              }}
            >
              <ArrowBack />
            </IconButton>

            <Title variant="h1">Welcome back</Title>
            <Subtitle>Sign in with your Google account to continue</Subtitle>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
            >
              <StyledButton
                startIcon={<Google />}
                onClick={handleGoogleLogin}
                disabled={loader.googleLoading}
              >
                {loader.googleLoading ? "Signing in..." : "Continue with Google"}
              </StyledButton>

              {error.open && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    width: "100%",
                    borderRadius: "16px",
                    border: "1px solid rgba(255, 77, 79, 0.16)",
                    backgroundColor: "rgba(255, 77, 79, 0.04)",
                    "& .MuiAlert-icon": {
                      color: "#ff4d4f",
                    },
                  }}
                >
                  {error.message}
                </Alert>
              )}
            </Box>

            <Typography
              sx={{
                mt: 5,
                fontSize: "13px",
                color: "#666",
                textAlign: "center",
                lineHeight: 1.6,
                maxWidth: "400px",
                mx: "auto",
              }}
            >
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </Typography>
          </Grid>
        </Fade>
      </Grid>
    </Box>
  );
}

export default withErrorBoundary(SignInScreen, {
  fallback: <GeneralErrorPage />,
});
